<script lang="ts" setup>
const { locale } = useI18n()
import { getPersonBySlug } from '~/queries/composer'
const { formatToFullDate } = useFullDateFormater()
const { slug } = useResourceSlug()
const handleHtmlClick = useHandleHtmlClick()
const { data, pending, error } = await useAsyncData(`/person/${slug}`, () =>
  execQuery(() => getPersonBySlug('workcourse'), {
    slug,
  })
)
const composer = computed<Composer>(() => {
  if (pending.value || error.value) return null
  return data.value.data.personBySlug
})

const workcourse = computed<Composer>(() => {
  if (!composer.value) return null
  return composer.value.workcourse
})
const updateDate = computed<string>(() => {
  if (!workcourse.value?.update_date) return null
  return formatToFullDate(workcourse.value.update_date.date)
})
const createDate = computed<string>(() => {
  if (!workcourse.value?.create_date) return null
  return formatToFullDate(workcourse.value.create_date.date)
})

const title = computed(() => {
return  `Parcours de l'oeuvre - ${composer.value?.firstName} ${composer.value?.lastName}`
})

const description = computed(() => {
return  `Le parcous de l'oeuvre du compositeur ${composer.value?.firstName} ${composer.value?.lastName}`
})

useHead({
  title,
  meta: [
    {
      name: 'description',
      content:description,
    }
  ],
})

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogImage: composer.value?.photo?.getUrl,
  twitterCard: 'summary',
})
</script>
<template>
  <DetailComposer
    :resume="
      locale === 'fr'
        ? composer?.resume
        : composer?.resumeEn ?? composer?.resume
    "
    :composer="composer"
    :active-outline-id="'workcourse'"
  >
    <template #typeIcon>
      <div
        class="m-14 hidden h-40 w-40 shrink-0 items-center justify-center bg-white sm:flex lg:h-60 lg:w-60"
        v-if="composer?.photo?.getUrl"
      >
        <div class="relative">
          <img
            :src="composer?.photo?.getUrl"
            :alt="
              'biography of ' + composer?.firstName + ' ' + composer?.lastName
            "
            class="h-full w-full object-cover sm:p-0"
          />
          <span
            v-if="composer?.copyrights"
            class="absolute right-1 bottom-0 text-white"
            >© {{ composer?.copyrights }}</span
          >
        </div>
      </div>
    </template>
    <template #prose="{ classes }">
      <div v-if="workcourse?.title">
        <h2 class="text-mobile-lg md:text-lg mt-0" v-if="locale === 'fr'">
          {{ workcourse?.title }}
        </h2>
        <h2 class="text-mobile-lg md:text-lg" v-else>
          {{ workcourse?.titleEn ?? workcourse?.title }}
        </h2>
      </div>
      <i18n-t
        v-if="
          workcourse?.authors &&
          (locale === 'fr' || (locale === 'en' && workcourse?.textEn !== ''))
        "
        keypath="labels.by_detail"
      >
        <template #name>
          <span class="font-bold">
            {{
              workcourse.authors
                .map(({ firstName, lastName }) => `${firstName} ${lastName}`)
                .join(', ')
            }}
          </span>
        </template>
      </i18n-t>
      <div
        v-if="locale === 'fr'"
        :class="classes"
        class="text-justify mt-4"
        v-html="workcourse?.text"
        @click="handleHtmlClick"
      ></div>
      <span
        v-else-if="
          locale === 'en' &&
          !workcourse?.textEn &&
          workcourse.translationStatus !== 'finished'
        "
        class="border border-2 border-amber-600 text-sm p-2 font-bold mx-2"
        >This text is being translated. We thank you for your patience.</span
      >
      <div
        v-else
        :class="classes"
        class="text-justify mt-4"
        v-html="
          workcourse?.textEn !== '' ? workcourse?.textEn : workcourse?.text
        "
        @click="handleHtmlClick"
      ></div>
      <div
        class="mt-8 -mb-10 text-base"
        v-if="workcourse?.translators?.length > 0 && locale !== 'fr'"
      >
        {{ $t('labels.translated_by') }}
        {{
          workcourse?.translators
            ?.map(({ lastName, firstName }) => `${firstName} ${lastName}`)
            .join(', ')
        }}
      </div>
    </template>
    <template #contentMisc>
      <h3
        v-if="workcourse?.source"
        class="mt-6 text-mobile-xl font-semibold first-letter:uppercase md:mt-10 md:text-lg"
      >
        {{ $t('labels.sources') }}
      </h3>
      <div
        v-if="workcourse?.source"
        class="prose prose-shade mt-2 max-w-none font-serif text-sm prose-a:no-underline"
        v-html="workcourse?.source"
        @click="handleHtmlClick"
      ></div>
      <hr class="mt-6 border-shade-200 md:mt-10" />
    </template>
    <template #sidebarDetails>
      <span
        v-if="workcourse?.update_date"
        class="inline-block first-letter:uppercase"
      >
        {{
          $t('labels.updated_at', {
            date: updateDate,
          })
        }}
      </span>
      <span
        v-else-if="workcourse?.create_date"
        class="inline-block first-letter:uppercase"
      >
        {{
          $t('labels.created_at', {
            date: createDate,
          })
        }}
      </span>
    </template>
  </DetailComposer>
</template>
